import { leadInPlainText } from "@/lib/lead-in-html"
import {
  authoringQuestionType,
  type AuthoringQuestionType,
} from "@/lib/library-authoring"
import { FILL_BLANK_MARKER } from "@/components/question-composer/fill-blank-html"

import type { QuestionQualityCheck, QuestionQualitySnapshot } from "./types"

const FORMAT_ALIASES: Array<{
  type: AuthoringQuestionType
  pattern: RegExp
}> = [
  { type: "fill_blank", pattern: /fill[\s-]*in[\s-]*(the[\s-]*)?blank/ },
  { type: "mcq_multiple", pattern: /multi[\s-]*select|multiple[\s-]*select|select all/ },
  {
    type: "mcq_single",
    pattern: /single[\s-]*choice|single[\s-]*select|multiple[\s-]*choice/,
  },
  { type: "true_false", pattern: /true\s*(\/|or)?\s*false/ },
  { type: "short_answer", pattern: /short[\s-]*answer/ },
  { type: "essay", pattern: /\bessay\b|long[\s-]*answer/ },
  { type: "numeric", pattern: /\bnumeric\b|\bnumerical\b/ },
  { type: "matching", pattern: /\bmatching\b|match the following/ },
  { type: "ordering", pattern: /\bordering\b|\bsequenc/ },
  { type: "hotspot", pattern: /\bhotspot\b/ },
]

export function parseFormatAsk(prompt: string): AuthoringQuestionType | null {
  const text = prompt.trim().toLowerCase()
  if (!text) return null
  const asksToChange =
    /convert|change|make (this|it)|turn (this|it)|switch|as a .+ format|want .{0,24}(in|as)|into /.test(
      text,
    ) || /\bformat\b/.test(text)
  if (!asksToChange) return null
  for (const row of FORMAT_ALIASES) {
    if (row.pattern.test(text)) return row.type
  }
  return null
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

export function fillBlankConversionFromSnapshot(
  snapshot: QuestionQualitySnapshot,
): { leadIn: string; accepted: string } {
  const stem = leadInPlainText(snapshot.leadIn).trim()
  const key =
    snapshot.options.find((option) => option.isCorrect)?.text.trim() ?? ""
  if (key) {
    const found = stem.match(new RegExp(escapeRegExp(key), "i"))
    if (found) {
      return {
        leadIn: stem.replace(found[0], FILL_BLANK_MARKER),
        accepted: key,
      }
    }
  }
  if (/\b(a|an|the)\s*$/i.test(stem)) {
    const next = stem.replace(/\b(a|an|the)\s*$/i, FILL_BLANK_MARKER).trim()
    return {
      leadIn: /[.?!]$/.test(next) ? next : `${next}.`,
      accepted: key,
    }
  }
  const whichTail = /\s+(which|what|who|how)\b[^?]*\??$/i
  if (whichTail.test(stem)) {
    const head = stem.replace(whichTail, "").replace(/[.:]+$/, "").trim()
    const accepted = key || snapshot.rationale.trim().split("\n")[0]?.trim() || ""
    return {
      leadIn: head ? `${head} ${FILL_BLANK_MARKER}.` : `${FILL_BLANK_MARKER}.`,
      accepted,
    }
  }
  const base = stem.replace(/[.:?]+$/, "")
  const accepted = key || snapshot.rationale.trim().split("\n")[0]?.trim() || ""
  return {
    leadIn: base ? `${base} ${FILL_BLANK_MARKER}.` : `${FILL_BLANK_MARKER}.`,
    accepted,
  }
}

export function formatConversionCheck(
  snapshot: QuestionQualitySnapshot,
  nextType: AuthoringQuestionType,
): QuestionQualityCheck {
  const nextLabel = authoringQuestionType(nextType).shortLabel
  const fromLabel = authoringQuestionType(
    snapshot.type as AuthoringQuestionType,
  ).shortLabel
  const fillBlank =
    nextType === "fill_blank" ? fillBlankConversionFromSnapshot(snapshot) : null
  return {
    id: `format-convert-${nextType}`,
    parameter: "format",
    severity: "caution",
    field: "settings",
    settingsTarget: "format",
    title: `Change format to ${nextLabel}`,
    mechanism:
      "Changing format creates a new item. Use this writes the new format on the canvas. Review every field before saving.",
    currentText: fromLabel,
    rubricConfidence: "high",
    actionKind: "change-format",
    actionLabel: "Change format",
    patch: {
      type: nextType,
      leadIn: fillBlank?.leadIn,
      fillBlankAccepted: fillBlank?.accepted ? [fillBlank.accepted] : undefined,
    },
    alternates: [
      {
        id: `to-${nextType}`,
        text: fillBlank
          ? `Change to ${nextLabel}. The keyed answer becomes the blank.`
          : `Change to ${nextLabel}. Finish the answer block on the canvas.`,
        actionKind: "change-format",
        actionLabel: "Change format",
        patch: {
          type: nextType,
          leadIn: fillBlank?.leadIn,
          fillBlankAccepted: fillBlank?.accepted
            ? [fillBlank.accepted]
            : undefined,
        },
        parameterDeltas: [
          { parameter: "format", effect: "clears" },
          { parameter: "cognitive", effect: "locked" },
        ],
        clearsCheckIds: [`format-convert-${nextType}`],
      },
    ],
  }
}

export function formatTypeAvailable(
  type: AuthoringQuestionType,
  capabilities: {
    numeric: boolean
    fillBlank: boolean
    matchingPairs: boolean
    ordering: boolean
    hotspot: boolean
  },
): boolean {
  if (type === "numeric") return capabilities.numeric
  if (type === "fill_blank") return capabilities.fillBlank
  if (type === "matching") return capabilities.matchingPairs
  if (type === "ordering") return capabilities.ordering
  if (type === "hotspot") return capabilities.hotspot
  return true
}

export function formatAskFollowUps(): string[] {
  return [
    "Change to Fill in the blank",
    "Change to Multi-select",
    "Keep this format and enhance the wording",
  ]
}

